import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { useContactData } from '@/hooks/useContactData';
import { useNavigationData } from '@/hooks/useNavigationData';
import { useSiteData } from '@/hooks/useSiteData';

export function History() {
  const { siteData } = useSiteData();
  const { contactInfo } = useContactData();
  const { navigationItems } = useNavigationData();

  return (
    <>
      <Helmet>
        <title>Club History - {siteData.title}</title>
        <meta
          name="description"
          content="The history of Wayland Rod & Gun Club from 1928 to present. Learn about our founding, early years, and development into the club we are today."
        />
        <meta name="keywords" content="history, Wayland Rod Gun Club, 1928, Dr. Stone, Waltham Rod Gun Club, heritage" />
      </Helmet>

      <Layout contactInfo={contactInfo} navigationItems={navigationItems}>
        <div className="p-4 sm:p-8 space-y-8 sm:space-y-12 max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-cursive text-primary-700 mb-4">
              Wayland Rod &amp; Gun Club - A Little History
            </h1>
          </div>

          {/* Historical Narrative Section */}
          <div className="space-y-6 text-base leading-relaxed font-body max-w-3xl mx-auto">
            <p className="text-neutral-700">
              In 1928 Waltham Rod &amp; Gun Club bought our current facilities, including 15 acres of land from Dr. Stone of Waltham, MA, an active member of the Club. Originally Dr. Stone lived on Stonebridge Road in Wayland. He moved to Waltham when he married, keeping the 15 acres of land the Club now occupies. About 1927 Dr. Stone thought it would be a nice surprise for his wife to build a house on his woodlands in Wayland. Ted Harrington (Wayland Fire Chief at the time and a freelance carpenter) was contracted to build this house. Completed in 1928 Dr. Stone brought his wife to see her new home. Although Mrs. Stone thought the house to be very nice she told her husband that he would never get her to live in the woods.
            </p>

            <p className="text-neutral-700">
              Dr. Stone, obviously upset that his plans fell flat, offered the Waltham Rod &amp; Gun Club the house and grounds at a price less than it cost him to build it. The terms were no money down with the good Doctor holding a ten year mortgage. Having moved into their new quarters the Waltham Rod &amp; Gun Club made an apartment out of the second floor of the house.
            </p>

            <p className="text-neutral-700">
              Now the club had to pay for what to them was a heavy load. At that time the membership of the Club was just about an even split between men and women. The ladies put on Whist and Beano parties every Saturday (open to the public) to help raise the funds necessary to pay the mortgage. The men organized six chicken parties a year; all events were open to the public. Some parties were dinners, some cook-outs, and some were raffles. The efforts of the Club members raised quite a lot of money. These functions usually brought in a minimum of 100 attendees. Each fall the Club put on a clambake. The clambake was like a fair with games for all ages, stands selling items, and raffles. Each Club member was assigned a task and any member not assisting in this annual event was dropped from the Club. Tickets for the annual clambake were limited to 225 and some years the Club had to turn people away. This one event alone raised between $500 and $1200. Other events that the Club organized were a Spring Fishing Derby and a Fall Hunt followed by a free dinner.
            </p>

            <p className="text-neutral-700">
              In 1948 the mortgage was paid off with a celebration of a big supper. Not only did the efforts of the membership pay off the mortgage, but the Club was left with about $600 in the treasury. Sadly, after the mortgage was paid off the volunteers began to fall off also. Around 1940-1941, the Club purchased some swamp land (about 20 acres) for a cost of $750.00 allowing the Club members to hunt and fish along the river and swamp. But around 1951 the Federal government purchased this land from the Club for a game preserve at a profit to the club of $9,250.
            </p>

            <p className="text-neutral-700">
              One of the reasons the Club was so effective was that any project involving the Club that was voted on was the responsibility of the person who proposed the project. Included in the responsibilities were getting the volunteers to do the work, raising the funds from the membership to cover the costs of equipment and supplies needed. The Club would sometimes advance funds for the project but the funds were to be replaced by fund raising, as the funds in the treasury were ear-marked for the purchase of hunting land in New Hampshire or Massachusetts.
            </p>

            <div className="border-t border-gray-200 pt-6 mt-8">
              <p className="text-sm text-neutral-600 italic">
                As recorded in the Wayland Rod &amp; Gun Club, Inc. By-Laws, Range Rules, General Information
                <br />
                Anyone with more information or pictures, please contact the webmaster or a club member.
              </p>
            </div>

            <div className="pt-4">
              <a
                href="http://wayland.wickedlocal.com/article/20160407/NEWS/160406823"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 font-medium underline"
              >
                Hidden gem in Wayland promotes safe gun handling
              </a>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}